// Location helpers shared between App.js and the Locations screens.
//
// These were module-level constants in App.js. They moved here so the extracted
// components in components/locations/ can use them without importing App.js,
// which would be circular. App.js re-exports nothing — it imports from here, so
// there is still exactly one definition of each.

// A location is sometimes a record and sometimes just its name string; both
// shapes appear in st.devices[].location and st.locations[].
export const locName = l => (typeof l === "string" ? l : l?.name ?? "");

// The virtual locations that represent bench state rather than a customer site.
// They are never shown in the Locations list and are never deploy destinations.
export const SYS_LOCS = ["In Stock", "In Testing", "Quarantine"];

export const isSysLoc = l => SYS_LOCS.includes(locName(l));

// Sites the Locations screens work with: real customer sites only.
export const siteLocations = (locations = []) => locations.filter(l => !isSysLoc(l));

// A site is archived either by the explicit archived flag or by the status the
// redesign surfaces as "Archived". Both spellings exist in stored state: older
// records used status "Retired", which the UI now calls Archived.
export const isArchived = l =>
  !!(l && (l.archived || l.status === "Archived" || l.status === "Retired"));

// The label the redesign shows for a site's status.
export const siteStatusLabel = l => (isArchived(l) ? "Archived" : "Active");

// Devices currently sitting on a given site.
export const devicesAtSite = (devices = [], location) => {
  const name = locName(location);
  return devices.filter(d => d.location === name);
};

// Same rule as normSerial in App.js:199 — kept local so this module stays importable
// by the Locations screens without reaching into App.js (see the header note above).
const normSerialLocal = s => String(s || "").trim().toLowerCase();

// The identity of an OWL portal project.
//
// OWL-BI restarts project numbering per company, so ProjectId alone collides: Company 1
// and Company 16 both have a "Project 54", and in the live data 7 project ids are shared
// across companies, covering 151 of 841 devices. Keying on ProjectId alone would map four
// unrelated customers' devices onto one site.
//
// THE single definition — portalIntent in App.js and deviceProject below must both call
// this. If the two ever compose the key differently the site grouping silently detaches
// from the Settings mapping, with no error anywhere.
//
// ":" is a safe separator: both ids are numeric in the API, so neither can contain one.
export const portalKey = (companyId, projectId) =>
  (companyId == null || projectId == null) ? null : String(companyId) + ":" + String(projectId);

// The OWL portal project a device is currently on, or null.
//
// The project is DERIVED, never stored on the device. It resolves only when the site the
// device is actually standing on matches the site its portal project is mapped to — which
// is what makes "a manual site move wins" automatic: move the device elsewhere and this
// returns null on the next render, with no field to clear at any of the ~46 places that
// write device.location.
//
// Returns {id, name, site} | null.
export const deviceProject = (device, snapshot, portalProjects) => {
  if (!device || !snapshot) return null;
  const site = device.location || "";
  // A bench or process state is never a project's home, even if a stale mapping points at
  // one. The Settings dropdown already excludes these, so this is a backstop.
  if (!site || isSysLoc(site)) return null;

  // Serial first, portal Id as the fallback — mirrors matchPortalDevice in App.js.
  let rec = snapshot.devices?.[normSerialLocal(device.serial)];
  if (!rec && device.deviceId) {
    const key = snapshot.byPortalId?.[String(device.deviceId)];
    rec = key ? snapshot.devices?.[key] : null;
  }
  const pkey = portalKey(rec && rec.companyId, rec && rec.projectId);
  if (!pkey) return null;

  const entry = (portalProjects || {})[pkey];
  if (!entry || !entry.location) return null;      // project not mapped to a site
  if (entry.location !== site) return null;        // a human moved it — their placement wins

  // The unnamed fallback must never leak the raw composite key ("16:54") into the UI.
  return { id: pkey, name: entry.name || "Project " + rec.projectId, site };
};

// Consumables held at a site, as { typeName: count }.
//
// Three independent sources, because the app holds consumables in more than one
// way and a site can hold several at once:
//   - st.consumableStock  tracked units, each with its own QR code, dispatched here
//   - st.consumableQty    untracked types, held as a count per location
//   - loc.batches[].consumables  counted items sent out with a dispatch batch
//
// `consumables` (the type list) is needed only to resolve the type ids used as keys
// in consumableQty back to names; without it that source is skipped, which keeps the
// older two-argument callers working unchanged.
export const consumablesAtSite = (loc, consumableStock = [], consumableQty = {}, consumables = []) => {
  const name = locName(loc);
  const held = {};

  (consumableStock || []).forEach(e => {
    if (e && e.status === "dispatched" && e.locationName === name && e.typeName) {
      held[e.typeName] = (held[e.typeName] || 0) + 1;
    }
  });

  const byId = {};
  (consumables || []).forEach(c => {
    if (c && c.id) byId[c.id] = c.name;
  });
  Object.entries((consumableQty || {})[name] || {}).forEach(([typeId, n]) => {
    const typeName = byId[typeId];
    if (typeName && n > 0) held[typeName] = (held[typeName] || 0) + n;
  });

  ((loc && loc.batches) || [])
    .filter(b => b.status === "active" || !b.status)
    .forEach(b => {
      Object.entries(b.consumables || {}).forEach(([k, v]) => {
        held[k] = (held[k] || 0) + (v || 0);
      });
    });

  return held;
};

// Total consumable items held at a site.
export const consumableTotal = (loc, consumableStock = [], consumableQty = {}, consumables = []) =>
  Object.values(consumablesAtSite(loc, consumableStock, consumableQty, consumables)).reduce(
    (a, v) => a + v,
    0
  );

// Which consumable types are individually serialised ("Coded" in the UI), so a
// site's holding can say which items carry their own QR code.
export const codedTypeNames = (consumableStock = []) => {
  const coded = new Set();
  (consumableStock || []).forEach(e => {
    if (e && e.serial && e.typeName) coded.add(e.typeName);
  });
  return coded;
};

// The date devices first landed on this site, from the oldest active batch.
// Returns null when nothing has been dispatched here.
export const devicesSince = loc => {
  const dates = ((loc && loc.batches) || [])
    .filter(b => (b.status === "active" || !b.status) && b.dispatchedAt)
    .map(b => new Date(b.dispatchedAt).getTime())
    .filter(t => !Number.isNaN(t));
  return dates.length ? new Date(Math.min(...dates)) : null;
};

// The most recent dispatch to this site, used for "Last deployment".
export const lastDeployment = loc => {
  const dates = ((loc && loc.batches) || [])
    .map(b => b.dispatchedAt && new Date(b.dispatchedAt).getTime())
    .filter(t => t && !Number.isNaN(t));
  return dates.length ? new Date(Math.max(...dates)) : null;
};

// "6 days ago" / "Yesterday" / "Today", as the mockup's Last deployment reads.
export const relativeDays = (date, now = Date.now()) => {
  if (!date) return "Never";
  const days = Math.floor((now - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return days + " days ago";
};

// en-GB throughout, matching every other date in the app.
export const formatDate = date =>
  date ? new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// First letter of the client name, for the index row avatar.
export const clientLetter = client => (client || "?").charAt(0).toUpperCase() || "?";

// Initials for an employee avatar.
export const initialsOf = name =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

// ─── Sub-locations ───────────────────────────────────────────────────────────
// A sub-location is an OWL portal project sitting within a site. It is DERIVED,
// never stored: st.portalProjects maps "<companyId>:<projectId>" -> {name, location},
// and every project whose `location` is this site is one of its sub-locations. That
// is the same source deviceProject reads, so the two can never disagree — there is
// no second list to keep in step with the portal.
//
// The "No project" bucket is not a sub-location and is never returned here; the
// site page adds it as a trailing group when devices land on a site without one.

// Sort key that keeps "No project" last wherever sub-locations are listed.
export const NO_SUB_LOCATION = '￿';

// The sub-locations of one site, as [{ id, name, site, companyId, projectId, deviceCount }].
// `devices` is the full device list; counts come from deviceProject so a device is
// counted under a project only when it is actually standing on that project's site.
export const subLocationsOf = (location, portalProjects = {}, snapshot = null, devices = []) => {
  const site = locName(location);
  if (!site || isSysLoc(site)) return [];

  const counts = new Map();
  (devices || []).forEach(d => {
    const p = deviceProject(d, snapshot, portalProjects);
    if (p && p.site === site) counts.set(p.id, (counts.get(p.id) || 0) + 1);
  });

  return Object.entries(portalProjects || {})
    .filter(([, e]) => e && e.location === site)
    .map(([id, e]) => {
      const [companyId, projectId] = String(id).split(':');
      return {
        id,
        // Mirrors deviceProject's fallback so an unnamed project never leaks "16:54".
        name: e.name || 'Project ' + projectId,
        site,
        companyId,
        projectId,
        deviceCount: counts.get(id) || 0,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Devices on a site that resolve to no portal project — the trailing "No project"
// group. Devices whose project is mapped to a DIFFERENT site land here too: they are
// standing on this site, which is what the site page is reporting.
export const devicesWithoutSubLocation = (location, portalProjects = {}, snapshot = null, devices = []) =>
  devicesAtSite(devices, location).filter(d => !deviceProject(d, snapshot, portalProjects));

// Every portal project that has devices in the snapshot but no site mapped yet.
//
// This is what the Locations review step offers to create: each row is a project the
// portal knows about that OWL Bench has nowhere to put. `company` is the site name the
// project would sit under, resolved the same way the Settings auto-map resolves it.
//
// Unnamed projects are included but flagged, because creating a sub-location called
// "Project 32" helps nobody — the caller decides whether to offer them.
export const unmappedSubLocations = (snapshot, portalProjects = {}, portalCompanies = {}) => {
  const rows = [];
  Object.entries((snapshot && snapshot.projects) || {}).forEach(([pkey, info]) => {
    const entry = (portalProjects || {})[pkey];
    if (entry && entry.location) return;                 // already sits within a site
    if (info.companyId == null) return;                  // nothing to sit under
    const name = (entry && entry.name) || '';
    rows.push({
      id: pkey,
      name,
      named: !!name.trim(),
      companyId: String(info.companyId),
      projectId: info.projectId == null ? '' : String(info.projectId),
      company: (portalCompanies || {})[String(info.companyId)] || 'Company ' + info.companyId,
      deviceCount: info.count || 0,
    });
  });
  // Biggest first — the projects holding the most kit are the ones worth placing.
  return rows.sort((a, b) => b.deviceCount - a.deviceCount || a.company.localeCompare(b.company));
};

// ─── Manual sub-locations ────────────────────────────────────────────────────
// Sub-locations that are STORED rather than derived, for a portal with no API behind
// it. A device names one in device.subLocation (by name, matching how device.location
// already works), because there is no snapshot to resolve it from.
//
// Two mechanisms now describe the same layer, so everything below funnels through
// subLocationsFor(). Nothing else should read st.subLocations directly.

// The stored sub-locations of one site.
export const storedSubLocationsOf = (location, subLocations = []) => {
  const site = locName(location);
  if (!site) return [];
  return (subLocations || []).filter(
    s => s && !s.archived && s.locationName === site
  );
};

// Every sub-location of a site: the stored ones and the portal-derived ones, as one
// list sorted by name.
//
// Where both describe the same place the PORTAL WINS, matched on portalKey. A stored
// record carrying a portalKey is a manual sub-location that has since been linked to a
// real project: the portal supplies its devices and its identity, and the stored record
// contributes only what the portal has no opinion about (its contact, its note). That is
// what makes the manual layer an on-ramp rather than a parallel universe — if the new
// portal gains an API, linking upgrades a record in place instead of duplicating it.
//
// `origin` is "portal" or "manual", which is what the UI badges.
export const subLocationsFor = (location, st = {}, devices = null) => {
  const site = locName(location);
  if (!site || isSysLoc(site)) return [];
  const devs = devices || st.devices || [];

  const derived = subLocationsOf(site, st.portalProjects, st.portalSnapshot, devs);
  const byPortalKey = new Map(derived.map(d => [d.id, d]));
  const stored = storedSubLocationsOf(site, st.subLocations);

  const out = derived.map(d => ({ ...d, origin: 'portal', stored: null }));

  stored.forEach(sub => {
    // Linked to a project the portal already returned: fold the stored extras onto the
    // derived row rather than adding a second row for the same place.
    if (sub.portalKey && byPortalKey.has(sub.portalKey)) {
      const row = out.find(r => r.id === sub.portalKey);
      row.stored = sub;
      row.contact = sub.contact; row.contactRole = sub.contactRole;
      row.phone = sub.phone; row.email = sub.email; row.note = sub.note;
      return;
    }
    out.push({
      id: sub.id,
      name: sub.name,
      site,
      companyId: null,
      projectId: null,
      // Counted by name off the device, since there is nothing to derive from.
      deviceCount: devs.filter(d => d.location === site && d.subLocation === sub.name).length,
      origin: 'manual',
      stored: sub,
      contact: sub.contact, contactRole: sub.contactRole,
      phone: sub.phone, email: sub.email, note: sub.note,
    });
  });

  return out.sort((a, b) => a.name.localeCompare(b.name));
};

// The sub-location a device sits in, from either mechanism. The stored name wins: it is
// an explicit placement someone made, where the derived one is inferred from the portal.
// Returns { id, name, origin } | null.
export const deviceSubLocation = (device, st = {}) => {
  if (!device) return null;
  const site = device.location || '';
  if (!site || isSysLoc(site)) return null;

  if (device.subLocation) {
    const hit = (st.subLocations || []).find(
      s => s && !s.archived && s.locationName === site && s.name === device.subLocation
    );
    // Named a sub-location that no longer exists: report the name rather than null, so a
    // device never silently loses its placement because a record was archived.
    return { id: hit ? hit.id : null, name: device.subLocation, origin: 'manual', orphaned: !hit };
  }

  const p = deviceProject(device, st.portalSnapshot, st.portalProjects);
  return p ? { id: p.id, name: p.name, origin: 'portal', orphaned: false } : null;
};

// Devices on a site that are in no sub-location at all, under either mechanism.
export const devicesWithNoSubLocation = (location, st = {}) =>
  devicesAtSite(st.devices || [], location).filter(d => !deviceSubLocation(d, st));

// Whether `name` is free as a sub-location of `site`. Names are the join key for the
// manual mechanism (device.subLocation holds a name), so a duplicate would merge two
// places into one silently.
export const subLocationNameFree = (name, site, st = {}, ignoreId = null) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  const clash = (st.subLocations || []).some(
    s => s && !s.archived && s.locationName === locName(site) &&
         s.id !== ignoreId && s.name.trim().toLowerCase() === n
  );
  if (clash) return false;
  // Also guard against a portal project of the same name, or the two would be
  // indistinguishable on the site page.
  return !subLocationsOf(site, st.portalProjects, st.portalSnapshot, [])
    .some(d => d.name.trim().toLowerCase() === n);
};
